import React, { useEffect, useState } from "react";
import Select from "react-select";
import CreateNewSupervisor from "../APL/CreateNewSupervisor";
import { GetSupervisorCompanies } from "../../actions/teacherQueries";
import { Card, CardHeader, Col, Row, Button, CardBody } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient, useMutation } from "react-query";
import { injectIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import EditNameDialog from "./EditNameDialogModal";
import { PostSupervisorCompany } from "../../actions/teacherMutations";
import { toast } from "react-toastify";
import AlertModal from "./AlertModal";
import { v4 as uuid } from "uuid";

const CreateAPLPeriodSupervisor = ({
  students,
  supervisors,
  aplStudentSupervisors,
  setAplStudentSupervisors,
  intl,
  schoolId,
  programTeachers,
  isOpen,
}) => {
  const [selectableSupervisors, setSelectableSupervisors] = useState([]);
  const [addNewSupervisorVisible, setAddNewSupervisorVisible] = useState(false);
  const [supervisorToAddToSchool, setSupervisorToAddToSchool] = useState(null);
  const [addToSchoolIsOpen, setAddToSchoolIsOpen] = useState(false);
  const [addNewCompanyIsOpen, setAddNewCompanyIsOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState(null);
  const [showCompanyNameOccupied, setShowCompanyNameOccupied] = useState(false);
  const [allStudentsSelected, setAllStudentsSelected] = useState(false);

  const companies = GetSupervisorCompanies();
  const postSupervisorCompanyMutation = useMutation(PostSupervisorCompany);
  const queryClient = useQueryClient();

  useEffect(() => {
    let tempSupervisorsForSelect = [];

    // Map and sort supervisors by name
    const sortedSupervisors = supervisors
      .map((sv) => ({
        value: sv,
        label: sv.name + " - " + sv.aplCompany.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    tempSupervisorsForSelect.push(...sortedSupervisors);

    let addNewSupervisor = {
      value: "Add",
      label: "--Lägg till ny handledare--",
    };
    tempSupervisorsForSelect.unshift(addNewSupervisor);

    // Map and sort program teachers by name
    const sortedTeachers = programTeachers
      .map((teacher) => ({
        label: teacher.name + " --Lärare--",
        value: { ...teacher, teacher: true },
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    tempSupervisorsForSelect.push(...sortedTeachers);

    setSelectableSupervisors(tempSupervisorsForSelect);
  }, [students, supervisors, aplStudentSupervisors, programTeachers]);

  useEffect(() => {
    if (isOpen) {
      const allStudentIds = new Set(students.map((s) => s.id));
      const existing = aplStudentSupervisors.filter((s) =>
        allStudentIds.has(s.id),
      );
      setAllStudentsSelected(existing.length === students.length);
    }
  }, [isOpen, aplStudentSupervisors, students]);

  const handleAddSupervisor = (stud, supervisor) => {
    if (supervisor.value === "Add") {
      setAddNewSupervisorVisible(true);
    } else {
      if (!aplStudentSupervisors.some((student) => student.id === stud.id)) {
        setAplStudentSupervisors([
          ...aplStudentSupervisors,
          {
            ...stud,
            supervisors: [{ ...supervisor.value, isMainResponsible: true }],
          },
        ]);
      } else if (
        aplStudentSupervisors.find((student) => student.id === stud.id)
          .supervisors.length < 1
      ) {
        let tempAplStudentSupervisors = [...aplStudentSupervisors];
        tempAplStudentSupervisors
          .find((student) => student.id === stud.id)
          .supervisors.push({ ...supervisor.value, isMainResponsible: true });
        setAplStudentSupervisors(tempAplStudentSupervisors);
      } else {
        let tempAplStudentSupervisors = [...aplStudentSupervisors];
        if (
          !tempAplStudentSupervisors
            .find((student) => student.id === stud.id)
            .supervisors.some((sv) => sv.id === supervisor.value.id)
        ) {
          tempAplStudentSupervisors
            .find((student) => student.id === stud.id)
            .supervisors.push({ ...supervisor.value });
          setAplStudentSupervisors(tempAplStudentSupervisors);
        }
      }
    }
  };

  const handleReloadSupervisors = () => {
    queryClient.invalidateQueries("FetchSupervisors");
  };

  const toggleAddToSchool = () => {
    setAddToSchoolIsOpen(!addToSchoolIsOpen);
  };

  const handleAddNewCompany = () => {
    let cleanedCompanyName = newCompanyName.replace("&", "%26");
    if (
      companies.data.some(
        (company) =>
          company.name.trim().toLowerCase() ===
          newCompanyName.trim().toLowerCase(),
      )
    ) {
      setShowCompanyNameOccupied(true);
    } else {
      postSupervisorCompanyMutation.mutate(cleanedCompanyName, {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            toast.success("Företag tillagt");
            queryClient.invalidateQueries("FetchSupervisorCompanies");
          } else {
            toast.warn(data);
          }
          setAddNewCompanyIsOpen(false);
        },
        throwOnError: true,
        onError: (data) => {
          toast.warn(data.response.data);
        },
      });
    }
  };

  const handleRemoveSupervisorFromStudent = (supervisorId, studentId) => {
    let tempAplStudentSupervisors = [...aplStudentSupervisors];
    tempAplStudentSupervisors.find(
      (stud) => stud.id === studentId,
    ).supervisors = tempAplStudentSupervisors
      .find((stud) => stud.id === studentId)
      .supervisors.filter((sv) => sv.id !== supervisorId);

    const student = tempAplStudentSupervisors.find(
      (stud) => stud.id === studentId,
    );

    if (student && student.supervisors.length > 0) {
      const firstSupervisor = student.supervisors[0];
      firstSupervisor.isMainResponsible = true;
      student.supervisors[0] = firstSupervisor;
    }
    setAplStudentSupervisors(tempAplStudentSupervisors);
  };

  const handleRemoveStudentFromAplStudents = (student) => {
    setAplStudentSupervisors(
      aplStudentSupervisors.filter((stud) => stud.id !== student.id),
    );
  };

  const handleAddStudentToAplStudents = (student) => {
    setAplStudentSupervisors([
      ...aplStudentSupervisors,
      { ...student, supervisors: [] },
    ]);
  };

  const handleSetSupervisorResponsable = (studentId, supervisorId) => {
    let tempAplStudentSupervisors = [...aplStudentSupervisors];
    let index = tempAplStudentSupervisors.findIndex(
      (stud) => stud.id === studentId,
    );
    tempAplStudentSupervisors[index].supervisors.forEach((sv) => {
      if (sv.id === supervisorId) {
        sv.isMainResponsible = true;
      } else {
        sv.isMainResponsible = false;
      }
    });
    setAplStudentSupervisors(tempAplStudentSupervisors);
  };

  const handleAddAllStudents = () => {
    const tempAplStudentSupervisors = [...aplStudentSupervisors];
    students.forEach((student) => {
      if (!tempAplStudentSupervisors.some((stud) => stud.id === student.id)) {
        tempAplStudentSupervisors.push({ ...student, supervisors: [] });
      }
    });
    setAplStudentSupervisors(tempAplStudentSupervisors);
  };

  const StudentSupervisors = ({ studentId }) => {
    const studentSupervisors = aplStudentSupervisors.some(
      (stud) => stud.id === studentId,
    )
      ? aplStudentSupervisors.find((stud) => stud.id === studentId).supervisors
      : [];

    return studentSupervisors.map((ssv) => {
      return (
        <Row className="p-2" key={uuid()}>
          <Col
            lg="1"
            style={{ cursor: "pointer" }}
            onClick={() => handleRemoveSupervisorFromStudent(ssv.id, studentId)}
          >
            <FontAwesomeIcon icon="trash-alt" color="red" />
          </Col>
          <Col lg="1">
            {ssv.isMainResponsible && (
              <FontAwesomeIcon className="me-2" icon="star" color="gold" />
            )}
          </Col>
          <Col
            style={{ cursor: "pointer" }}
            onClick={() => handleSetSupervisorResponsable(studentId, ssv.id)}
          >
            <span style={{ verticalAlign: "text-top" }}>
              {ssv.name}{" "}
              {ssv.aplCompany?.name ? " - " + ssv.aplCompany.name : ""}
            </span>
          </Col>
        </Row>
      );
    });
  };

  const StudentCards = () => {
    return students.map((stud) => {
      const studentIsAdded = aplStudentSupervisors.some(
        (student) => student.id === stud.id,
      );
      return (
        <Col lg="4" key={uuid()}>
          <Card>
            <CardHeader>
              <Col
                style={{ cursor: "pointer" }}
                onClick={() =>
                  studentIsAdded
                    ? handleRemoveStudentFromAplStudents(stud)
                    : handleAddStudentToAplStudents(stud)
                }
              >
                <div
                  style={{
                    border: "1px solid black",
                    width: "23px",
                    height: "23px",
                    display: "inline-block",
                    alignContent: "center",
                    verticalAlign: "top",
                  }}
                  className="rounded ps-1 me-2"
                >
                  {studentIsAdded && (
                    <FontAwesomeIcon icon="check" className={"text-success"} />
                  )}
                </div>

                {stud.name}
              </Col>
              <Col></Col>
            </CardHeader>
            <CardBody>
              <Select
                menuPortalTarget={document.body}
                menuPosition={"fixed"}
                placeholder={intl.formatMessage({ id: "ChooseSupervisor" })}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                name="students"
                options={selectableSupervisors}
                onChange={(event) => handleAddSupervisor(stud, event)}
              />
            </CardBody>
            <StudentSupervisors studentId={stud.id} />
          </Card>
        </Col>
      );
    });
  };

  if (companies.isLoading) {
    return null;
  }

  return (
    <>
      <Col lg={12} className="my-2">
        <FontAwesomeIcon className="me-2" icon="star" color="gold" />
        <FormattedMessage id="OneSupervisorHasToBeResponsableInfo" />
      </Col>
      <Row className="my-3">
        <Col>
          <Button
            outline
            color="primary"
            onClick={() => {
              allStudentsSelected
                ? setAplStudentSupervisors([])
                : handleAddAllStudents();
              setAllStudentsSelected(!allStudentsSelected);
            }}
          >
            {allStudentsSelected ? (
              <FormattedMessage id="DeselectAllStudents" />
            ) : (
              <FormattedMessage id="ChooseAllStudents" />
            )}
          </Button>
        </Col>
      </Row>
      {addNewSupervisorVisible ? (
        <CreateNewSupervisor
          dismissModal={() => setAddNewSupervisorVisible(false)}
          reloadSupervisors={handleReloadSupervisors}
          selectableSupervisors={selectableSupervisors}
          setSelectableSupervisors={setSelectableSupervisors}
          supervisorToAddToSchool={supervisorToAddToSchool}
          setSupervisorToAddToSchool={setSupervisorToAddToSchool}
          addToSchoolIsOpen={addToSchoolIsOpen}
          setAddToSchoolIsOpen={setAddToSchoolIsOpen}
          toggleAddToSchool={toggleAddToSchool}
          schoolId={schoolId}
        />
      ) : (
        ""
      )}
      <Row>
        <StudentCards />
      </Row>
      <EditNameDialog
        toggle={() => setAddNewCompanyIsOpen(!addNewCompanyIsOpen)}
        isOpen={addNewCompanyIsOpen}
        title={"Lägg till företag"}
        onChange={(val) => setNewCompanyName(val)}
        defaultName={null}
        onProceed={(val) => handleAddNewCompany(val)}
      />
      <AlertModal
        close={() => setShowCompanyNameOccupied(false)}
        isOpen={showCompanyNameOccupied}
        title={intl.formatMessage({ id: "CompanyAlreadyExists" })}
        message={intl.formatMessage({ id: "CompanyAlreadyExistsExplanation" })}
      />
    </>
  );
};
export default injectIntl(CreateAPLPeriodSupervisor);
